<template>
  <div id="sample-detail" v-if="!isLoading">
    <b-breadcrumb>
      <b-breadcrumb-item text="Samples" to="/samples"/>
      <b-breadcrumb-item :text="`${ sample.labSampleReference }`" :active="true"/>
    </b-breadcrumb>
    <b-card header-tag="header" header-class="no-border">
      <template slot="header">
        <h2>
          <font-awesome-icon icon="vials" class="icon"/>
          {{ sample.labSampleReference }}
        </h2>
        <span class="urgent-flag" v-if="job && job.priority.toLowerCase().trim() == 'urgent'">
          <font-awesome-icon icon="flag" fixed-width class="icon"/>Urgent
        </span>
      </template>
      <div class="job-status d-flex align-items-center">
        <div
          v-if="job"
          class="badge badge-lg"
          :class="'badge-'+job.status.toLowerCase().trim().replace(/ /g,'-')"
        >{{ job.status }}</div>
        <div>
          <span class="label">Priority</span>
          <span class="value">{{ job.priority }}</span>
        </div>
        <div>
          <span class="label">Job Reference</span>
          <router-link tag="a" :to="`/jobs/${ job.jobId }`">
            <strong>
              <span class="value">{{ job.labRegistrationReference }}</span>
              <font-awesome-icon icon="angle-right" fixed-width class="icon"/>
            </strong>
          </router-link>
        </div>
        <div>
          <span class="label">Sample Date</span>
          <span
            class="value"
            v-if="sample.sampleDate"
          >{{ sample.sampleDate | moment("D MMM YYYY") }}</span>
          <span class="value" v-if="!sample.sampleDate">-</span>
        </div>
      </div>
      <b-row>
        <b-col class="col-border-right">
          <dl>
            <dt>Client Reference</dt>
            <dd>{{ job.clientRegistrationReference || '-' }}</dd>
            <dt>Client</dt>
            <dd>{{ job.client.name }}</dd>
            <dt>Contact</dt>
            <dd>{{ job.client.firstName }} {{ job.client.lastName }}</dd>
          </dl>
        </b-col>
        <b-col class="col-border-right">
          <dl>
            <dt v-if="showField('matrix')">Matrix</dt>
            <dd v-if="showField('matrix')">{{ sample.matrix || '-' }}</dd>
            <dt>Sample Condition</dt>
            <dd>{{ sample.sampleCondition || '-' }}</dd>
            <dt v-if="showField('samplingDepth')">Sampling Depth</dt>
            <dd v-if="showField('samplingDepth')">{{ sample.samplingDepth || '-' }}</dd>
            <dt v-if="showField('samplesInComposite')">Samples in Composite</dt>
            <dd v-if="showField('samplesInComposite')">{{ sample.samplesInComposite || '-' }}</dd>
          </dl>
        </b-col>
        <!--



        -->
        <b-col>
          <dl>
            <dt>Composite Reference</dt>
            <dd>{{ sample.compositeReference || '-' }}</dd>
            <dt v-if="showField('batchReference')">Batch Reference</dt>
            <dd v-if="showField('batchReference')">{{ sample.batchReference || '-' }}</dd>
            <dt v-if="showField('cowNumber')">Cow Number</dt>
            <dd v-if="showField('cowNumber')">{{ sample.cowNumber || '-' }}</dd>
            <dt v-if="showField('factoryId')">Factory ID</dt>
            <dd v-if="showField('factoryId')">{{ sample.factoryId || '-' }}</dd>
          </dl>
        </b-col>
      </b-row>
    </b-card>

    <b-tabs class="mt-4">
      <!-- Results Tab -->
      <b-tab>
        <template slot="title">
          <font-awesome-icon icon="chart-pie" class="icon"/>Results
        </template>
        <ResultsTable :jobId="getJobId" :sampleId="getSampleId" class="mt-4"/>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import { EventBus } from "@/services/event-bus.js";
import ResultsTable from "@/components/ResultsTable.vue";
import { getSample, getJob } from "@/services/api";
import { getSampleFields } from "@/services/localstorage";

export default {
  name: "SampleDetail",
  components: {
    ResultsTable
  },
  data() {
    return {
      isLoading: true,
      jobId: null,
      sampleId: null,
      job: { labRegistrationReference: "" },
      sample: { labSampleReference: "" },
      breadcrumbs: [
        {
          text: "Samples",
          href: "/samples"
        }
      ],
      selectedFields: {}
    };
  },
  mounted: function() {
    EventBus.$emit("showLoader");
    /**
     * This hides fields based on column selection in the samples table - as per AN-205
     *
     * Fields that are ignored:
     *  clientSampleReference       - not used here
     *  labSampleReference          - no can do - it's the page header
     *  sampleDate                  - this seems pointless
     *  job.registrationDescription - not used here
     * Note: Some of these are in the results table - but not hiding the fields there.
     *
     * Fields that cannot be toggled:
     *  - sample condition
     *  - composite reference
     *
     * Fields that WILL hide:
     *   matrix
     *   samplingDepth
     *   samplesInComposite
     *   batchReference
     *   cowNumber
     *   factoryId
     *
     **/
    const selection = getSampleFields();
    if(selection) {
      // converting the selection to an object here - easier to work with
      selection.forEach(key => {
        this.selectedFields[key] = true;
      });
    } else {
      this.initToDefaultFields();
    }
    let self = this;

    return getSample(this.$route.params.jobId, this.$route.params.sampleId)
      .then(sample => {
        self.sample = sample;
        self.sampleId = sample.sampleId;
        self.jobId = self.$route.params.jobId;

        return getJob(self.jobId);
      })
      .then(job => {
        self.job = job;

        if (job) {
          EventBus.$emit("hideLoader");
          self.isLoading = false;
        }
      });
  },
  methods: {
    showField: function(field) {
      if(this.selectedFields[field]) {
        // it's selected, show it
        return true;
      }
      // hide it
      return false;
    },
    // new users won't have this set, so fill in the defaults
    initToDefaultFields: function() {
      const fields = [ 'sampleDate', 'clientSampleReference', 'labSampleReference', 'job.registrationDescription', 'matrix' ];

      fields.forEach(key => {
        this.selectedFields[key] = true;
      });
    }
  },
  computed: {
    getJobId: function() {
      return this.jobId;
    },
    getSampleId: function() {
      return this.sampleId;
    }
  }
};
</script>
<style lang="scss">
@import "../assets/styles/main.scss";
.urgent-flag {
  margin-right: 0;
  margin-left: auto;
  font-size: 1.2rem;
  color: $danger;
  .icon {
    margin-right: 5px;
    font-size: 1.2rem;
  }
}
.job-status {
  border-bottom: 1px solid $ui-gray-lighter;
  border-top: 1px solid $ui-gray-lighter;
  padding: 20px 0px;
  margin-bottom: 20px;
  div {
    margin-right: 50px;
    span.label {
      display: block;
      font-size: 0.8rem;
      color: $text-color-light;
      text-transform: uppercase;
      font-weight: $font-weight-semibold;
    }
    span.value {
      font-size: 1.2rem;
      font-weight: $font-weight-semibold;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/SampleDetail.vue